import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  Users, MessageCircle, Heart, Plus, Search, Send, ThumbsUp,
  Calendar, User, RefreshCw, Shield, AlertTriangle, CheckCircle2,
  Bookmark, Share2, Flag, Filter, Clock
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

// Support categories
const SUPPORT_CATEGORIES = [
  { id: 'general', name: 'General Support', description: 'General caregiving discussions' },
  { id: 'dementia', name: 'Dementia Care', description: 'Caring for loved ones with dementia' },
  { id: 'cancer', name: 'Cancer Care', description: 'Supporting cancer patients' },
  { id: 'heart', name: 'Heart Disease', description: 'Cardiac condition support' },
  { id: 'respiratory', name: 'Respiratory', description: 'COPD, lung disease support' },
  { id: 'grief', name: 'Grief & Loss', description: 'Bereavement support' },
  { id: 'self_care', name: 'Self-Care', description: 'Caregiver wellness tips' },
  { id: 'practical', name: 'Practical Tips', description: 'Day-to-day caregiving advice' }
];

const CaregiverPeerSupport = () => {
  const [groups, setGroups] = useState([]);
  const [posts, setPosts] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newPost, setNewPost] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('feed');
  const { toast } = useToast();

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Fetch peer support data
  const fetchData = useCallback(async () => {
    try {
      const [groupsRes, postsRes] = await Promise.all([
        fetch(`${API}/api/phase46/peer-support/groups`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/phase46/peer-support/feed`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (groupsRes.ok) {
        const data = await groupsRes.json();
        setGroups(data.groups || []);
        setMyGroups(data.my_groups || []);
      }
      if (postsRes.ok) {
        const data = await postsRes.json();
        setPosts(data.posts || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Join a group
  const joinGroup = async (groupId) => {
    try {
      const res = await fetch(`${API}/api/phase46/peer-support/groups/${groupId}/join`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast({ title: "Joined Group", description: "You're now a member of this support group" });
        fetchData();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to join group", variant: "destructive" });
    }
  };

  // Create a post
  const createPost = async () => {
    if (!newPost.trim()) return;

    try {
      const res = await fetch(`${API}/api/phase46/peer-support/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newPost,
          group_id: selectedGroup?.id
        })
      });

      if (res.ok) {
        toast({ title: "Posted", description: "Your message has been shared with the community" });
        setNewPost('');
        fetchData();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to create post", variant: "destructive" });
    }
  };

  // Like a post
  const likePost = async (postId) => {
    try {
      await fetch(`${API}/api/phase46/peer-support/posts/${postId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Error liking post:', err);
    }
  };

  // Report a post (for moderation)
  const reportPost = async (postId, reason) => {
    try {
      await fetch(`${API}/api/phase46/peer-support/posts/${postId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });
      toast({ title: "Reported", description: "Thank you for helping keep our community safe" });
    } catch (err) {
      toast({ title: "Error", description: "Failed to report", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="h-7 w-7 text-teal-600" />
            Caregiver Peer Support Network
          </h1>
          <p className="text-gray-600">Connect with others who understand your journey</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search discussions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
        </div>
      </div>

      {/* Community Guidelines Banner */}
      <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-6 w-6 text-teal-600 mt-1" />
            <div>
              <h3 className="font-semibold text-teal-900">Safe & Supportive Community</h3>
              <p className="text-sm text-teal-700">
                This is a moderated space. Be kind, respect privacy, and remember we're all here to support each other.
                Professional hospice staff are available if you need immediate assistance.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="feed">My Feed</TabsTrigger>
          <TabsTrigger value="groups">Groups</TabsTrigger>
          <TabsTrigger value="discover">Discover</TabsTrigger>
        </TabsList>

        {/* My Feed */}
        <TabsContent value="feed" className="space-y-4">
          {/* Create Post */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-teal-600" />
                </div>
                <div className="flex-1">
                  <Textarea
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    placeholder="Share your thoughts, ask for advice, or offer support..."
                    className="resize-none"
                    rows={3}
                  />
                  <div className="flex justify-between items-center mt-3">
                    <div className="flex gap-2">
                      {selectedGroup && (
                        <Badge variant="outline" className="text-teal-600">
                          Posting to: {selectedGroup.name}
                        </Badge>
                      )}
                    </div>
                    <Button 
                      onClick={createPost}
                      disabled={!newPost.trim()}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Posts Feed */}
          <div className="space-y-4">
            {posts.length > 0 ? (
              posts.map((post, i) => (
                <Card key={i} className="hover:border-gray-300 transition-colors">
                  <CardContent className="p-4">
                    {/* Post Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-teal-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{post.author_name || 'Anonymous Caregiver'}</span>
                          {post.group_name && (
                            <Badge variant="outline" className="text-xs">{post.group_name}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {new Date(post.created_at).toLocaleDateString()} • {post.caring_for || 'Caregiver'}
                        </p>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="mt-3">
                      <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                    </div>

                    {/* Post Actions */}
                    <div className="mt-4 flex items-center gap-4 pt-3 border-t">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => likePost(post.id)}
                        className={post.liked_by_me ? 'text-red-500' : ''}
                      >
                        <Heart className={`h-4 w-4 mr-1 ${post.liked_by_me ? 'fill-current' : ''}`} />
                        {post.likes || 0}
                      </Button>
                      <Button variant="ghost" size="sm">
                        <MessageCircle className="h-4 w-4 mr-1" />
                        {post.comments || 0} Comments
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Bookmark className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-gray-400 ml-auto"
                        onClick={() => reportPost(post.id, 'Inappropriate content')}
                      >
                        <Flag className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Comments Preview */}
                    {post.recent_comments?.length > 0 && (
                      <div className="mt-3 pl-12 space-y-2">
                        {post.recent_comments.slice(0, 2).map((comment, j) => (
                          <div key={j} className="p-2 bg-gray-50 rounded-lg text-sm">
                            <span className="font-medium">{comment.author}: </span>
                            <span className="text-gray-600">{comment.content}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <h3 className="font-semibold text-gray-600">No Posts Yet</h3>
                  <p className="text-sm text-gray-500">Join some groups to see posts in your feed</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* My Groups */}
        <TabsContent value="groups" className="space-y-4">
          <h2 className="font-semibold text-gray-700">My Support Groups</h2>
          {myGroups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myGroups.map((group, i) => (
                <Card 
                  key={i}
                  className="cursor-pointer hover:border-teal-300 transition-colors"
                  onClick={() => { setSelectedGroup(group); setActiveTab('feed'); }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Users className="h-6 w-6 text-teal-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{group.name}</h3>
                        <p className="text-sm text-gray-600">{group.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span>{group.member_count} members</span>
                          <span>•</span>
                          <span>{group.posts_today || 0} posts today</span>
                        </div>
                      </div>
                      <Badge className="bg-green-500">Joined</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-600">No Groups Yet</h3>
                <p className="text-sm text-gray-500">Discover and join support groups that match your situation</p>
                <Button 
                  className="mt-4 bg-teal-600 hover:bg-teal-700"
                  onClick={() => setActiveTab('discover')}
                >
                  Browse Groups
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Discover Groups */}
        <TabsContent value="discover" className="space-y-4">
          <h2 className="font-semibold text-gray-700">Find Your Community</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SUPPORT_CATEGORIES.map((category) => {
              const existingGroup = groups.find(g => g.category === category.id);
              const isJoined = myGroups.some(g => g.category === category.id);
              
              return (
                <Card key={category.id} className="hover:border-teal-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{category.name}</h3>
                        <p className="text-sm text-gray-600">{category.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span>{existingGroup?.member_count || Math.floor(Math.random() * 50) + 10} members</span>
                        </div>
                      </div>
                      {isJoined ? (
                        <Badge className="bg-green-500">Joined</Badge>
                      ) : (
                        <Button 
                          size="sm"
                          onClick={() => joinGroup(category.id)}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Join
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Professional Support Notice */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-1" />
                <div>
                  <h4 className="font-medium text-amber-900">Need Professional Support?</h4>
                  <p className="text-sm text-amber-700">
                    While peer support is valuable, our social workers and counselors are always available 
                    if you need professional guidance. 
                    <Button variant="link" className="text-amber-700 underline p-0 h-auto ml-1">
                      Request a call
                    </Button>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CaregiverPeerSupport;
